import {Autocomplete, AutocompleteRenderGroupParams, ClickAwayListener, Tooltip} from "@mui/material";
import {FilterOptionsState} from "@mui/material/useAutocomplete";
import React, {useState} from "react";
import TextField from "../textfield/TextField";
import {SelectOption} from "./SelectOption";

export type LabeledComponent = {
    label: string,
    component: JSX.Element | string,
}

interface MultiSelectFieldProps<T> {
    id?: string;
    label?: string;
    options: T[];
    values: T[];
    onChange: (changedValues: T[]) => void;
    getRenderLabel: (value: T) => LabeledComponent | string;
    isOptionEqualToValue?: (option: T, value: T | null) => boolean;
    filterOptions?: (options: T[], state: FilterOptionsState<T>) => T[];
    groupBy?: (option: T) => string;
    renderGroup?: (params: AutocompleteRenderGroupParams) => React.ReactNode;
    required?: boolean;
    readOnly?: boolean;
    helperText?: string;
    errorMessage?: string;
    limitTags?: number;
}

export default function MultiSelectField<T>(props: Readonly<MultiSelectFieldProps<T>>) {
    const [opened, setOpened] = useState<boolean>(false);

    function buildTooltip() {
        return <div>
            {props.values
                .map(option => renderLabel(option))
                .map(label => <div key={label}>{label}</div>)
            }
        </div>
    }

    function normalizeRenderLabel(option: T): LabeledComponent {
        if (props.getRenderLabel) {
            const renderedLabel = props.getRenderLabel(option);
            if (typeof renderedLabel === 'string') {
                return {
                    label: renderedLabel,
                    component: renderedLabel,
                }
            } else {
                return renderedLabel;
            }
        } else {
            const label = JSON.stringify(option);
            return {
                label: label,
                component: label,
            }
        }
    }

    function renderLabel(option: T): string {
        return normalizeRenderLabel(option).label;
    }

    const limitTags = props.limitTags ?? 1;
    const isTooltipShown = limitTags > 0 && props.values.length > limitTags;

    const autocomplete = (
            <Autocomplete
                    id={props.id}
                    open={opened}
                    onOpen={() => setOpened(true)}
                    onClose={() => setOpened(false)}
                    value={props.values}
                    onChange={(e, values) => {
                        props.onChange(values);
                        e.stopPropagation();
                    }}
                    options={props.options}
                    getOptionLabel={(option) => renderLabel(option as T)}
                    isOptionEqualToValue={props.isOptionEqualToValue}
                    renderOption={(props, option, {selected}) => (
                            <li {...props}>
                                <SelectOption option={option} label={renderLabel(option)} checked={selected}/>
                            </li>
                    )}
                    filterOptions={props.filterOptions}
                    groupBy={props.groupBy}
                    renderGroup={props.renderGroup}
                    disableCloseOnSelect={true}
                    readOnly={props.readOnly}
                    multiple={true}
                    freeSolo={false}
                    size={"small"}
                    limitTags={props.limitTags !== undefined ? props.limitTags : 1}
                    forcePopupIcon={!(props.readOnly)}
                    renderInput={(params) => (
                            <TextField {...params}
                                    label={props.label}
                                    helperText={props.helperText}
                                    variant="outlined"
                                    required={props.required}
                                    errorMessage={props.errorMessage}/>
                    )}/>
    );

    return (
            <ClickAwayListener onClickAway={() => setOpened(false)}>
                <div>
                    {isTooltipShown &&
                            <Tooltip title={buildTooltip()} arrow={true}>
                                {autocomplete}
                            </Tooltip>
                    }
                    {!isTooltipShown && autocomplete}
                </div>
            </ClickAwayListener>
    );
}
